import { Link } from "react-router-dom";
import { Heart, ShieldCheck, Sprout, HandHeart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Reveal, Overline } from "@/components/Reveal";
import OrgChart from "@/components/OrgChart";
import { FOUNDERS_PHOTO } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";

const VALUE_ICONS = [Heart, ShieldCheck, Sprout, HandHeart];

export default function About() {
  const { t } = useLang();
  const a = t.about;

  return (
    <>
      {/* Page hero */}
      <section className="bg-[#0A2540] text-white" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 sm:py-28">
          <Reveal>
            <Overline className="text-[#4ade80]">{a.heroOverline}</Overline>
            <h1 className="mt-4 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl leading-[1.05]">{a.heroTitle}</h1>
            <p className="mt-6 text-lg text-white/80 max-w-2xl leading-relaxed">{t.mission}</p>
          </Reveal>
        </div>
      </section>

      {/* History */}
      <section className="py-20 sm:py-28" data-testid="history-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-start">
          <Reveal className="lg:sticky lg:top-28">
            <figure>
              <img src={FOUNDERS_PHOTO} alt="Founders Pastor Patricia Shelton and the late Bishop James E. Shelton" className="rounded-2xl shadow-xl w-full object-cover aspect-[4/5]" />
              <figcaption className="mt-4 text-sm text-[#475569]">
                <span className="font-semibold text-[#0A2540]">{a.foundersLabel}</span>{a.foundersCaption}
              </figcaption>
            </figure>
          </Reveal>
          <Reveal delay={0.15}>
            <Overline>{a.storyOverline}</Overline>
            <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl text-[#0A2540] tracking-tight">{a.storyTitle}</h2>
            <div className="mt-6 space-y-4 text-[#475569] text-lg leading-relaxed">
              {a.history.map((para, i) => (<p key={i}>{para}</p>))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 sm:py-28 bg-[#0A2540] text-white" data-testid="timeline-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <Overline className="text-[#4ade80]">{a.journeyOverline}</Overline>
            <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl tracking-tight">{a.journeyTitle}</h2>
          </Reveal>
          <ol className="mt-14 relative border-l-2 border-white/15 space-y-12">
            {a.timeline.map((item, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <li className="relative pl-8 sm:pl-12" data-testid={`timeline-item-${i}`}>
                  <span className="absolute -left-[9px] top-1.5 w-4 h-4 rounded-full bg-[#E85D04] ring-4 ring-[#0A2540]" aria-hidden="true" />
                  <div className="font-display font-extrabold text-2xl sm:text-3xl text-[#4ade80]">{item.year}</div>
                  <p className="mt-2 text-white/80 leading-relaxed">{item.text}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 sm:py-28 bg-[#F1F5F9]" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <Overline>{a.valuesOverline}</Overline>
            <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl text-[#0A2540] tracking-tight">{a.valuesTitle}</h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {a.values.map((v, i) => {
              const Icon = VALUE_ICONS[i];
              return (
                <Reveal key={i} delay={i * 0.08}>
                  <Card className="h-full bg-white border-[#E2E8F0] rounded-2xl p-7 shadow-sm hover:-translate-y-1 hover:shadow-md transition-all">
                    <div className="w-12 h-12 rounded-xl bg-[#E85D04]/10 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-[#E85D04]" aria-hidden="true" />
                    </div>
                    <h3 className="mt-5 font-display font-semibold text-lg text-[#0A2540]">{v.title}</h3>
                    <p className="mt-2 text-sm text-[#475569] leading-relaxed">{v.text}</p>
                  </Card>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Board & Staff — interactive org chart */}
      <section className="py-20 sm:py-28" data-testid="board-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <Overline>{a.peopleOverline}</Overline>
            <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl text-[#0A2540] tracking-tight">{a.peopleTitle}</h2>
            <p className="mt-4 text-[#475569] text-lg">{a.peopleSub}</p>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <OrgChart />
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#F1F5F9]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#0A2540]">{a.ctaTitle}</h2>
          <p className="mt-3 text-[#475569]">{a.ctaBody}</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="bg-[#E85D04] hover:bg-[#CC5202] text-white font-semibold rounded-full h-12 px-8">
              <Link to="/donate" data-testid="about-donate-button">{a.donate}</Link>
            </Button>
            <Button asChild variant="outline" className="border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540] hover:text-white font-semibold rounded-full h-12 px-8">
              <Link to="/get-involved" data-testid="about-involved-button">{a.getInvolved}</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
